import { Row } from "@amzn/stencil-react-components/layout";
import { SkeletonButton } from "@amzn/stencil-react-components/skeleton";
import { Text } from "@amzn/stencil-react-components/text";
import { IconArrowRight, IconRefresh, IconSize } from "@amzn/stencil-react-icons";
import Button from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import Chip, { ChipBackgroundColor, ChipIconPosition } from "@amzn/uxm-stencil-react-components/lib/src/stencil/chip";
import { getString } from "common/uistringlabels/uiStringUtils";
import { sortingOptions } from "lib/models/aiforme-models";
import { JobInfo } from "lib/models/oui-models";
import React, { useEffect } from "react";

import { AiForMeSearchListState } from "./useAiForMeSearchList";
import useAiForMeUseCases from "./useAiForMeUseCases";

export interface AiForMeUseCasesProps {
  updateQuery: (query: string) => void;
  batchUpdateInputs: (updates: Partial<AiForMeSearchListState>) => void;
  isNewLandingPage: boolean;
  amazonianName?: string;
  jobInfo?: JobInfo;
  onUseCaseSearch?: (query: string) => void;
}

export const AiForMeUseCases = (props: AiForMeUseCasesProps) => {
  const { updateQuery, batchUpdateInputs, isNewLandingPage = false, amazonianName, jobInfo, onUseCaseSearch } = props;
  const { isLoadingUseCases, useCases, getUseCases, scrambleUseCases } = useAiForMeUseCases();

  useEffect(() => {
    if (jobInfo) {
      getUseCases(jobInfo.jobFamilyName);
    }
  }, [jobInfo]);

  const roleRecommendedUseCasesPath = isNewLandingPage
    ? "aiForMe.landingPage.roleRecommendedUseCases"
    : "aiForMe.roleRecommendedUseCases";

  return (
    <>
      <Row alignItems="center" style={{ paddingBottom: 16, gap: 8 }}>
        {isNewLandingPage && (
          <img src="/img/ant-design_smile-outlined.svg" alt="Smiley icon" id="use-cases-smiley-svg" />
        )}
        <Text>
          {getString(roleRecommendedUseCasesPath, { amazonianName: amazonianName ?? "", role: jobInfo?.businessTitle })}
        </Text>
      </Row>
      {isLoadingUseCases && (
        <>
          <Row columnGap={20} padding={[0, 0, 18, 0]} alignItems="flex-end">
            <SkeletonButton width="20%" />
            <SkeletonButton width="20%" />
            <SkeletonButton width="20%" />
            <SkeletonButton width="15%" />
          </Row>
        </>
      )}
      {!isLoadingUseCases && useCases && (
        <>
          <Row columnGap={20} padding={[0, 0, 18, 0]} alignItems="flex-end">
            {useCases.slice(0, 3).map((useCase, index) => (
              <Chip
                key={index}
                touchpoint="use-case-click"
                data-testid={`use-case-${index}`}
                customData={{
                  name: useCase.name,
                  amazonianInfo: {
                    businessTitle: jobInfo?.businessTitle,
                    jobFamilyName: jobInfo?.jobFamilyName,
                  },
                }}
                onClick={() => {
                  if (onUseCaseSearch) {
                    onUseCaseSearch(useCase.name);
                  } else {
                    updateQuery(useCase.name);
                    batchUpdateInputs({
                      searchText: useCase.name,
                      selectedJobFamilies: [],
                      sortBy: sortingOptions[0],
                      isUseCaseSearch: true,
                    });
                  }
                }}
                backgroundColor={ChipBackgroundColor.Default}
                iconPosition={ChipIconPosition.Trailing}
                icon={<IconArrowRight aria-hidden={true}></IconArrowRight>}
              >
                {useCase.name}
              </Chip>
            ))}
            <Button
              key="shuffle-use-cases"
              icon={<img src="/img/lucide_shuffle.svg" alt="Shuffle icon" />}
              touchpoint="shuffle-use-case-click"
              data-testid="shuffle-use-cases"
              onClick={() => {
                scrambleUseCases();
              }}
            >
              Shuffle
            </Button>
          </Row>
        </>
      )}
    </>
  );
};
